import {FixedFooter} from 'components/common/FixedFooter'
import {Button} from 'components/system/Button'
import {CheckboxInputField} from 'components/system/fields/CheckboxInputField'
import {PhoneInputField} from 'components/system/fields/PhoneInputField'
import {StandardInputField} from 'components/system/fields/StandardInputField'
import {PhoneNumberUtil} from 'google-libphonenumber'
import {useGa4MountEffect} from 'hooks/useGa4MountEffect'
import {useNextStepperPath} from 'hooks/useNextStepperPath'
import {useUpdateOrderMutation} from 'hooks/ventrata/useUpdateOrderMutation'
import {some, trim} from 'lodash'
import {useCurrency, useTermsPath, useTravelerPath} from 'providers/EnvironmentProvider'
import {useContact, useCrossSelling, useOrderDetailsMutators} from 'providers/OrderDetailsProvider'
import {OrderProvider} from 'providers/OrderProvider'
import {useCallback, useState} from 'react'
import {FieldMetaState, Form} from 'react-final-form'
import {ParsedCountry} from 'react-international-phone'
import {Basic, Params, Ventrata} from 'types'
import addTrackingBreadcrumb, {BreadcrumbLevel, BreadcrumbType} from 'utils/addTrackingBreadcrumb'
import {getGa4OrderItems} from 'utils/getGa4OrderItems'
import {getHumanPrice} from 'utils/getHumanPrice'
import setTrackingUser from 'utils/setTrackingUser'

const phoneUtil = PhoneNumberUtil.getInstance()

const EMAIL_RE =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

const validateEmail = (value: any) => {
  const trimmedValue = trim(value)
  if (!trimmedValue) {
    return 'Email address is empty'
  }
  return trimmedValue.match(EMAIL_RE) ? undefined : 'Invalid email address'
}

const validatePhone = (value: any) => {
  try {
    if (!phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(value))) {
      return 'Invalid phone number'
    }
  } catch (error) {
    return 'Invalid phone number'
  }
}

const validateFirstName = (value: any) => {
  const trimmedValue = trim(value)
  return trimmedValue ? undefined : 'First name is empty'
}

const validateLastName = (value: any) => {
  const trimmedValue = trim(value)
  return trimmedValue ? undefined : 'Last name is empty'
}

const validateAgreedBookingTerms = (value: any) => {
  return value ? undefined : 'Required'
}

const validate = (values: Record<string, any>) => {
  return {
    firstName: validateFirstName(values.firstName),
    lastName: validateLastName(values.lastName),
    emailAddress: validateEmail(values.emailAddress),
    phoneNumber: validatePhone(values.phoneNumber),
    agreedBookingTerms: validateAgreedBookingTerms(values.agreedBookingTerms),
  }
}

const ButtonSpinner = () => (
  <div role="status" className="align-baseline mr-4">
    <svg
      aria-hidden="true"
      className="inline w-6 h-6 text-green-800 animate-spin dark:text-green-900 fill-white"
      viewBox="0 0 100 101"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
        fill="currentColor"
      />
      <path
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
        fill="currentFill"
      />
    </svg>
    <span className="sr-only">Loading...</span>
  </div>
)

const showError = (meta: FieldMetaState<unknown>) => {
  const {active, submitError, modifiedSinceLastSubmit, error, touched} = meta

  if (!active && !modifiedSinceLastSubmit && submitError) {
    return submitError
  }

  if (error && touched) {
    return error
  }

  return undefined
}

export const Screen = () => {
  const currency = useCurrency()
  const order = OrderProvider.useOrder()
  const crossSelling = useCrossSelling()
  const termsPath = useTermsPath()
  const travelerPath = useTravelerPath()
  const nextPath = useNextStepperPath(travelerPath)
  const contact = useContact()
  const {setContact} = useOrderDetailsMutators()
  const updateOrderMutation = useUpdateOrderMutation()
  const updateOrder = updateOrderMutation.mutate
  const [contactSubmitting, setContactSubmitting] = useState<boolean>(false)
  const [phoneCountry, setPhoneCountry] = useState<string | null>(null)

  const initialValues = {
    ...contact,
  }

  useGa4MountEffect({
    event: 'begin_checkout',
    ecommerce: {
      currency,
      value: getHumanPrice(order.pricing?.retail),
      checkout_type: 'custom_checkout',
      items: getGa4OrderItems(order, currency, crossSelling),
    },
  })

  const onSubmit = useCallback(
    (values: Record<string, any>) => {
      const errors = validate(values)

      if (some(errors, Boolean)) {
        return errors
      }

      setContactSubmitting(true)

      const contact = {
        ...values,
        country: phoneCountry,
      }

      setContact(contact as Basic.Contact)
      setTrackingUser((contact as Basic.Contact).emailAddress)
      addTrackingBreadcrumb({
        type: BreadcrumbType.Default,
        level: BreadcrumbLevel.Info,
        message: 'User set contact details',
        data: contact as Basic.Contact,
      })

      const updateParams: Params.UpdateOrderRequest = {
        orderId: order.id,
        contact: contact as Ventrata.Contact,
      }

      updateOrder(updateParams, {
        onSuccess: () => {
          window.location.href = nextPath
        },

        onError: () => {
          setContactSubmitting(false)
        },
      })
    },
    [nextPath, setContact, updateOrder, order.id, phoneCountry, setContactSubmitting]
  )

  const onPhoneChange = useCallback(
    (number: string, meta: {country: ParsedCountry; inputValue: string}) => {
      if (meta.country && meta.country.iso2) {
        setPhoneCountry(meta.country.iso2.toUpperCase())
      }
    },
    [setPhoneCountry]
  )

  return (
    <div className="flex flex-col gap-6">
      <h3 className="font-bold text-2xl">Traveler contact details</h3>
      <Form
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={({handleSubmit}) => {
          return (
            <form onSubmit={handleSubmit} className="flex flex-col gap-6" id="contacts">
              <div className="flex flex-col gap-4">
                <StandardInputField
                  id="firstName"
                  label="First name"
                  type="text"
                  name="firstName"
                  spellCheck={false}
                  showError={showError}
                  validate={validateFirstName}
                />
                <StandardInputField
                  id="lastName"
                  label="Last name"
                  type="text"
                  name="lastName"
                  spellCheck={false}
                  showError={showError}
                  validate={validateLastName}
                />
                <StandardInputField
                  id="emailAddress"
                  label="Email address"
                  type="email"
                  name="emailAddress"
                  spellCheck={false}
                  showError={showError}
                  validate={validateEmail}
                />
                <PhoneInputField
                  id="phoneNumber"
                  label="Phone number"
                  name="phoneNumber"
                  onChange={onPhoneChange}
                  showError={showError}
                  validate={validatePhone}
                />
              </div>

              <CheckboxInputField
                id="agreedBookingTerms"
                name="agreedBookingTerms"
                label={
                  <div className="text-[15px]">
                    I agree to the{' '}
                    <a href={termsPath} target="_blank" rel="noreferrer" className="text-dark-blue hover:opacity-80">
                      booking and cancellation terms
                    </a>
                  </div>
                }
              />

              <CheckboxInputField
                id="allowMarketing"
                name="allowMarketing"
                label={<span className="text-[15px]">Send me the latest offers and exclusive updates</span>}
              />

              <FixedFooter>
                <div className="flex gap-2"></div>
                <Button.Primary.Orange
                  type="submit"
                  expanded
                  form="contacts"
                  disabled={contactSubmitting}
                  className={contactSubmitting ? '!bg-green-700' : ''}
                >
                  {contactSubmitting ? (
                    <>
                      <ButtonSpinner />
                      Creating your order, please wait a moment...
                    </>
                  ) : (
                    'Next'
                  )}
                </Button.Primary.Orange>

                <div className="mt-8 text-xs text-gray-400">
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a className="underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a className="underline" href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
                    Terms of Service
                  </a>{' '}
                  apply.
                </div>
              </FixedFooter>
            </form>
          )
        }}
      />
    </div>
  )
}
